import React, { useState, useEffect } from 'react';import ReactDOM from 'react-dom';import API_BASE_URL from '../config';import styles from '../css-isolated/sa_edit_cell.module.css';
import authFetch from '../utils/authFetch';

function SaEditCell({ isDarkMode }) {
  const [cells, setCells] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [pendingDeleteCell, setPendingDeleteCell] = useState(null);
  const [formData, setFormData] = useState({
    typeOfCell: '',
    cellNo: '',
    capacity: '',
    note: ''
  });

  // Fetch all cells from the database
  useEffect(() => {
    fetchCells();
  }, []);

  const fetchCells = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/cells`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setCells(data.data);
      } else {
        setErrors({ general: data.message || 'Failed to fetch cells.' });
      }
    } catch (error) {
      console.error('Fetch cells error:', error);
      setErrors({ general: 'Network error. Please check your connection.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (cell) => {
    setEditingCell(cell.id);
    setFormData({
      typeOfCell: cell.type_of_cell,
      cellNo: cell.cell_no,
      capacity: cell.capacity,
      note: cell.note || ''
    });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setFormData({
      typeOfCell: '',
      cellNo: '',
      capacity: '',
      note: ''
    });
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.typeOfCell.trim()) {
      newErrors.typeOfCell = 'Type of cell is required';
    }

    if (!formData.cellNo.trim()) {
      newErrors.cellNo = 'Cell number is required';
    }

    if (!formData.capacity) {
      newErrors.capacity = 'Capacity is required';
    } else if (parseInt(formData.capacity) < 1) {
      newErrors.capacity = 'Capacity must be at least 1';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async (cellId) => {
    if (!validateForm()) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/api/cells/${cellId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type_of_cell: formData.typeOfCell,
          cell_no: formData.cellNo,
          capacity: parseInt(formData.capacity),
          note: formData.note || null
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors) {
          const backendErrors = {};
          Object.keys(data.errors).forEach(key => {
            const fieldMap = {
              'type_of_cell': 'typeOfCell',
              'cell_no': 'cellNo',
              'capacity': 'capacity',
              'note': 'note'
            };
            const reactKey = fieldMap[key] || key;
            backendErrors[reactKey] = data.errors[key][0];
          });
          setErrors(backendErrors);
          return;
        }
        throw new Error(data.message || 'Failed to update cell');
      }

      setModalMessage(`Cell ${formData.cellNo} updated successfully!`);
      setShowSuccessModal(true);
      setEditingCell(null);
      fetchCells();
    } catch (error) {
      console.error('Update cell error:', error);
      setModalMessage(error.message || 'Failed to update cell. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleDelete = (cellId, cellNo) => {
    setPendingDeleteCell({ id: cellId, cellNo: cellNo });
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteCell) return;

    const { id: cellId, cellNo } = pendingDeleteCell;
    setShowConfirmModal(false);

    try {
      const response = await authFetch(`${API_BASE_URL}/api/cells/${cellId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setModalMessage(`Cell ${cellNo} deleted successfully!`);
        setShowSuccessModal(true);
        fetchCells();
      } else {
        setModalMessage(data.message || 'Failed to delete cell. Please try again.');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Delete cell error:', error);
      setModalMessage('Network error. Please check your connection and try again.');
      setShowErrorModal(true);
    } finally {
      setPendingDeleteCell(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setPendingDeleteCell(null);
  };

  // Filter cells based on search term
  const filteredCells = cells.filter(cell => 
    cell.type_of_cell?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cell.cell_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cell.note?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`${styles.contentWrapper} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      {/* Main Form Section */}
      <div className={styles.formSection}>
        
        {/* Section Header with Icon */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h3 className={styles.sectionTitle}>Cell Management</h3>
            <div className={styles.sectionAccent}></div>
          </div>
        </div>

        {/* Error Alert */}
        {errors.general && (
          <div className={styles.errorAlert}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {errors.general}
          </div>
        )}

        {/* Search and Controls */}
        <div className={styles.controlsSection}>
          <div className={styles.searchContainer}>
            <div className={styles.searchInputContainer}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <input
                type="text"
                placeholder="Search cells by type, number, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </div>
          
          <button
            onClick={fetchCells}
            className={styles.refreshButton}
            disabled={isLoading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M3 12C3 12 3 4.5 10.5 4.5C18 4.5 21 12 21 12" stroke="currentColor" strokeWidth="2"/>
              <path d="M21 12C21 12 21 19.5 13.5 19.5C6 19.5 3 12 3 12" stroke="currentColor" strokeWidth="2"/>
              <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2"/>
              <path d="M17 16L21 12L17 8" stroke="currentColor" strokeWidth="2"/>
            </svg>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* Cells Table */}
        <div className={styles.tableContainer}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <svg width="40" height="40" className={styles.loadingSpinner} viewBox="0 0 24 24" fill="none">
                <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <p>Loading cells...</p>
            </div>
          ) : filteredCells.length === 0 ? (
            <div className={styles.emptyState}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <p>No cells found</p>
              {searchTerm && <p>Try adjusting your search criteria</p>}
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.cellsTable}>
                <thead>
                  <tr>
                    <th>Type of Cell</th>
                    <th>Cell Number</th>
                    <th>Capacity</th>
                    <th>Current Occupancy</th>
                    <th>Notes</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCells.map(cell => (
                    editingCell === cell.id ? (
                      <tr key={cell.id} className={styles.editingRow}>
                        <td>
                          <select
                            name="typeOfCell"
                            value={formData.typeOfCell}
                            onChange={handleInputChange}
                            className={`${styles.editInput} ${errors.typeOfCell ? styles.error : ''}`}
                          >
                            <option value="">Select type</option>
                            <option value="Isolation Cell">Isolation Cell</option>
                            <option value="Male Cell">Male Cell</option>
                            <option value="Female Cell">Female Cell</option>
                          </select>
                          {errors.typeOfCell && <span className={styles.errorText}>{errors.typeOfCell}</span>}
                        </td>
                        <td>
                          <input
                            type="text"
                            name="cellNo"
                            value={formData.cellNo}
                            onChange={handleInputChange}
                            className={`${styles.editInput} ${errors.cellNo ? styles.error : ''}`}
                            placeholder="Cell number"
                          />
                          {errors.cellNo && <span className={styles.errorText}>{errors.cellNo}</span>}
                        </td>
                        <td>
                          <input
                            type="number"
                            name="capacity"
                            value={formData.capacity}
                            onChange={handleInputChange}
                            className={`${styles.editInput} ${errors.capacity ? styles.error : ''}`}
                            placeholder="Capacity"
                            min="1"
                          />
                          {errors.capacity && <span className={styles.errorText}>{errors.capacity}</span>}
                        </td>
                        <td className={styles.occupancyCell}>{cell.current_occupancy}</td>
                        <td>
                          <textarea
                            name="note"
                            value={formData.note}
                            onChange={handleInputChange}
                            className={styles.editTextarea}
                            placeholder="Notes"
                            rows="2"
                          />
                        </td>
                        <td className={styles.actionCell}>
                          <div className={styles.actionButtons}>
                            <button
                              onClick={() => handleUpdate(cell.id)}
                              className={styles.saveButton}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/>
                                <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2"/>
                                <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className={styles.cancelButton}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2"/>
                                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={cell.id} className={styles.tableRow}>
                        <td className={styles.typeCell}>
                          <span className={`${styles.typeBadge} ${
                            cell.type_of_cell === 'Male Cell' ? styles.typeMale :
                            cell.type_of_cell === 'Female Cell' ? styles.typeFemale :
                            styles.typeIsolation
                          }`}>
                            {cell.type_of_cell}
                          </span>
                        </td>
                        <td className={styles.cellNoCell}>{cell.cell_no}</td>
                        <td className={styles.capacityCell}>{cell.capacity}</td>
                        <td className={styles.occupancyCell}>
                          <div className={styles.occupancyInfo}>
                            <span className={styles.occupancyNumber}>{cell.current_occupancy}</span>
                            <span className={styles.occupancyBar}>
                              <span 
                                className={styles.occupancyFill}
                                style={{ 
                                  width: `${(cell.current_occupancy / cell.capacity) * 100}%`,
                                  backgroundColor: 
                                    (cell.current_occupancy / cell.capacity) > 0.9 ? '#ef4444' :
                                    (cell.current_occupancy / cell.capacity) > 0.7 ? '#f59e0b' :
                                    '#22c55e'
                                }}
                              ></span>
                            </span>
                          </div>
                        </td>
                        <td className={styles.noteCell}>{cell.note || '—'}</td>
                        <td className={styles.actionCell}>
                          <div className={styles.actionButtons}>
                            <button
                              onClick={() => handleEdit(cell)}
                              className={styles.editButton}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(cell.id, cell.cell_no)}
                              className={styles.deleteButton}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary Info */}
        {!isLoading && filteredCells.length > 0 && (
          <div className={styles.summarySection}>
            <div className={styles.summaryStats}>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>{filteredCells.length}</span>
                <span className={styles.statLabel}>Total Cells</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>
                  {filteredCells.reduce((sum, cell) => sum + cell.capacity, 0)}
                </span>
                <span className={styles.statLabel}>Total Capacity</span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statNumber}>
                  {filteredCells.reduce((sum, cell) => sum + cell.current_occupancy, 0)}
                </span>
                <span className={styles.statLabel}>Current Occupancy</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`}>
          <div className={styles.confirmModal}>
            <div className={styles.confirmIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Delete Cell?</h3>
            <p>Are you sure you want to delete cell <strong>{pendingDeleteCell?.cellNo}</strong>?</p>
            <p className={styles.warningText}>This action cannot be undone.</p>
            <div className={styles.confirmActions}>
              <button onClick={cancelDelete} className={styles.cancelBtn}>Cancel</button>
              <button onClick={confirmDelete} className={styles.confirmBtn}>Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Success Modal */}
      {showSuccessModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={() => setShowSuccessModal(false)}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Success!</h3>
            <p>{modalMessage}</p>
            <button className={styles.successBtn} onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Error Modal */}
      {showErrorModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${!isDarkMode ? 'lightMode' : ''}`} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{modalMessage}</p>
            <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SaEditCell;
